/**
 * Configuration-driven form system types
 * Designed for runtime JSON loading with Modbus batch writes
 */

// ============ Field Types ============

export type FieldType = 'input' | 'select' | 'checkbox' | 'time' | 'date' | 'weekday' | 'trigger' | 'bit-toggle' | 'bit-select';

export interface SelectOption {
  value: number | string;
  label: string;
}

export interface FieldConstraint {
  min?: number;
  max?: number;
  step?: number;
  options?: SelectOption[];
  label?: string; // For trigger type
}

// ============ Cross-Field Extensions ============

/** Cross-field transform: value depends on other fields */
export interface CrossFieldTransformConfig {
  name: string;
  dependencies: string[];
  config?: Record<string, any>;
}

/** Dynamic options: select options depend on other fields */
export interface DynamicOptionsConfig {
  name: string;
  dependencies: string[];
  config?: Record<string, any>;
}

export type CrossFieldTransformFn = (
  value: any,
  direction: 'read' | 'write',
  dependencies: Record<string, any>,
  config?: Record<string, any>,
) => any;

export type DynamicOptionsFn = (
  currentValue: any,
  dependencies: Record<string, any>,
  config?: Record<string, any>,
) => SelectOption[];

export type CustomTriggerHandlerFn = (context: {
  command: any;
  registerTables: Record<string, any>;
  field: FieldDefinition;
  section: ModeSection;
}) => Promise<void>;

// ============ Field Definition ============

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  inputType?: 'number' | 'text';
  constraint?: FieldConstraint;
  unit?: string;
  /** Register key in Modbus table */
  registerKey: string;
  /** Transform to apply: 'time' | 'date' | 'weekday' | 'multiply' | 'divide' */
  transform?: FieldTransformType;
  transformFactor?: number;
  /** Condition for visibility: e.g., { field: 'WORK_MODE', value: 1 } */
  visibleWhen?: VisibilityCondition;
  /** InverterSettingsCode enum value name */
  settingsCode?: string;
  /** Value to write when a trigger-type field is activated (default: 1) */
  triggerValue?: number;
  /** Cross-field transform applied on read/write */
  crossFieldTransform?: CrossFieldTransformConfig;
  /** Dynamic select options that depend on other field values */
  dynamicOptions?: DynamicOptionsConfig;
  /** Name of custom trigger handler (provided by host via customTriggerHandlers input) */
  customTriggerHandler?: string;
  /** bit-toggle: bit position (0-15) in the parent register */
  bitIndex?: number;
  /** bit-select: [startBit, length] range in the parent register */
  bitRange?: [number, number];
  /** Register key that contains the bits (the "parent" register) */
  bitRegisterKey?: string;
}

export type FieldTransformType = 'time' | 'date' | 'weekday' | 'multiply' | 'divide' | 'writeMultiply' | 'none';

export interface VisibilityCondition {
  field: string;
  value: number | number[];
  operator?: 'eq' | 'neq' | 'in';
}

// ============ Mode/Section ============

export interface BuiltSectionSelector {
  key: string;
  label: string;
  registerKey: string;
  options: SelectOption[];
  registerTable: string;
  applyMode?: 'manual' | 'immediate';
  alwaysIncludeInSectionWrites?: boolean;
}

export interface ModeSection {
  id: string;
  label: string;
  /** Which WORK_MODE value activates this section */
  modeValue?: number;
  fields: FieldDefinition[];
  /** Control register key to trigger write (e.g., 'TOU_Control') */
  controlRegister?: string;
  /** Register table name */
  registerTable: string;
  /** Validity mask name */
  validityMask?: string;
  /** If true, do NOT save mode selector when saving this section (default: false = save mode if dirty) */
  excludeModeSelectorOnSave?: boolean;
  /** Per-section selector (e.g., rule ID) that can trigger a section re-read */
  sectionSelector?: BuiltSectionSelector;
  /** Delegate entire section content to a host-provided ng-template */
  customContent?: string;
  /** Expand write range to full aligned blocks of this size (in registers).
   *  Safety registers require 64-register block writes. */
  writeBlockSize?: number;
}

// ============ Presets ============

export interface Preset {
  id: string;
  label: string;
  description?: string;
  /** Ionicon name (e.g. 'sunny-outline', 'moon-outline') */
  icon?: string;
  /** Material icon name (e.g. 'wb_sunny'). Falls back to `icon` if absent. */
  matIcon?: string;
  /** Field values: { fieldKey: value } — arrays for time/date/weekday fields */
  values: Record<string, number | string | number[] | string[]>;
  /** Optional: only show for specific modes */
  forModes?: number[];
}

// ============ Form Configuration ============

export interface ConfigFormSchema {
  /** Schema version for future compatibility */
  version: string;
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Main mode selector field (e.g., WORK_MODE) */
  modeSelector?: {
    key: string;
    label: string;
    registerKey: string;
    options: SelectOption[];
    registerTable: string;
    settingsCode?: string;
    /** If true, always prepend modeSelector register in section writes (ensures fixed start address) */
    alwaysIncludeInSectionWrites?: boolean;
    /** 'manual' = Apply button (default), 'immediate' = auto-apply on selection change */
    applyMode?: 'manual' | 'immediate';
  };
  /** Sections by mode */
  sections: ModeSection[];
  /** Available presets */
  presets?: Preset[];
}

// ============ Events ============

export interface ConfigFormSaveEvent {
  sectionId: string;
  values: Record<string, any>;
  transformedValues: Record<string, number>;
  controlRegister?: string;
  /** True if mode selector was also saved with this section */
  modeSelectorSaved?: boolean;
  /** True if section selector was saved (host should re-read the section) */
  sectionSelectorSaved?: boolean;
}

export interface ConfigFormErrorEvent {
  /** Type of operation that failed */
  type: 'save-section' | 'save-mode' | 'load-config';
  /** Section ID if applicable */
  sectionId?: string;
  /** Error message */
  message: string;
  /** Original error object */
  error?: any;
}

// ============ Lean Form Definitions (V2: TypeScript-driven) ============

export interface LeanFieldRef {
  registerKey: string;
  key?: string;
  visibleWhen?: VisibilityCondition;
  crossFieldTransform?: CrossFieldTransformConfig;
  dynamicOptions?: DynamicOptionsConfig;
  labelKey?: string;
  fieldType?: FieldType;
  constraint?: FieldConstraint;
  transform?: FieldTransformType;
  transformFactor?: number;
  unit?: string;
  triggerValue?: number;
  inputType?: 'number' | 'text';
  settingsCode?: string;
  customTriggerHandler?: string;
  /** bit-toggle: bit position (0-15) in the parent register */
  bitIndex?: number;
  /** bit-select: [startBit, length] range in the parent register */
  bitRange?: [number, number];
  /** Register key that contains the bits (defaults to registerKey if omitted) */
  bitRegisterKey?: string;
}

export interface LeanSectionSelector {
  registerKey: string;
  key?: string;
  labelKey?: string;
  registerTable?: string;
  applyMode?: 'manual' | 'immediate';
  alwaysIncludeInSectionWrites?: boolean;
  options?: SelectOption[];
}

export interface LeanModeSection {
  id: string;
  labelKey?: string;
  label?: string;
  modeValue?: number;
  fields: LeanFieldRef[];
  controlRegister?: string;
  registerTable: string;
  validityMask?: string;
  excludeModeSelectorOnSave?: boolean;
  sectionSelector?: LeanSectionSelector;
  customContent?: string;
  /** Expand write range to full aligned blocks of this size (in registers).
   *  Safety registers require 64-register block writes. */
  writeBlockSize?: number;
}

export interface LeanFormSchema {
  version: string;
  id: string;
  name: string;
  labelMap?: Record<string, string>;
  modeSelector?: {
    registerKey: string;
    key?: string;
    labelKey?: string;
    label?: string;
    registerTable: string;
    settingsCode?: string;
    alwaysIncludeInSectionWrites?: boolean;
    applyMode?: 'manual' | 'immediate';
    options?: SelectOption[];
  };
  sections: LeanModeSection[];
  presets?: Preset[];
}

// ============ Custom Section Context ============

export interface CustomSectionContext {
  section: ModeSection;
  initialData: Record<string, any>;
  command: any;
  registerTables: Record<string, any>;
  isSaving: boolean;
  labels: Record<string, string>;
}

// ============ Lean Form Helpers ============

import { RegisterTable } from 'ngx-modbus';

/**
 * Type-safe field reference helper.
 * @param table - Register table object, only used for type inference (constrains `registerKey` to valid keys)
 * @param registerKey - Key from the register table
 * @param overrides - Optional field definition overrides
 */
export function fieldRef<T extends Partial<RegisterTable>>(
  table: T,
  registerKey: keyof T & string,
  overrides?: Omit<LeanFieldRef, 'registerKey'>,
): LeanFieldRef {
  return { registerKey, ...overrides };
}

/**
 * Type-safe bit-toggle field reference.
 * Creates a virtual field `{registerKey}_bit{bitIndex}` backed by a single bit in the parent register.
 */
export function bitToggleRef<T extends Partial<RegisterTable>>(
  table: T,
  registerKey: keyof T & string,
  bitIndex: number,
  overrides?: Omit<LeanFieldRef, 'registerKey' | 'bitIndex' | 'fieldType'>,
): LeanFieldRef {
  return {
    registerKey,
    bitIndex,
    bitRegisterKey: overrides?.bitRegisterKey ?? registerKey,
    fieldType: 'bit-toggle',
    key: `${registerKey}_bit${bitIndex}`,
    ...overrides,
  };
}

/**
 * Type-safe bit-select field reference.
 * Creates a virtual field backed by a range of bits [startBit, length] in the parent register.
 */
export function bitSelectRef<T extends Partial<RegisterTable>>(
  table: T,
  registerKey: keyof T & string,
  bitRange: [number, number],
  overrides?: Omit<LeanFieldRef, 'registerKey' | 'bitRange' | 'fieldType'>,
): LeanFieldRef {
  return {
    registerKey,
    bitRange,
    bitRegisterKey: overrides?.bitRegisterKey ?? registerKey,
    fieldType: 'bit-select',
    key: `${registerKey}_bits${bitRange[0]}_${bitRange[1]}`,
    ...overrides,
  };
}
